/**
 * "Request a call": a lead with an Inbox task. Used when nothing is open,
 * for emergencies (15-minute SLA, OB-10) and when the office asks a patient
 * to call (OB-17).
 */
import type { Attribution } from '@velora/contracts/booking';
import { TURNSTILE_ACTIONS, type TurnstilePublic } from '@velora/contracts/turnstile';
import { Button, Checkbox, Field, Input, Textarea } from '@velora/ui';
import { CheckCircle2, Phone } from 'lucide-react';
import { useRef, useState, type FormEvent } from 'react';
import { messageOf, type BookingApi } from '../lib/api.js';
import { formatPhoneInput, isMobile, telHref } from '../lib/format.js';
import { BOT_WAIT_MESSAGE, BotCheck, botTokenMissing, type TurnstileHandle } from './Turnstile.js';

interface Props {
  api: BookingApi;
  visitorId: string;
  practiceName: string;
  officePhone: string | null;
  officePhoneLabel: string | null;
  kind: 'callback' | 'emergency' | 'must_call';
  typeId?: string | null;
  attribution: Attribution;
  /** Bot check for the request (`site.turnstile`). */
  bot?: TurnstilePublic | undefined;
  title: string;
  lead: string;
  /** Prefill from what the visitor already typed. */
  firstName?: string;
  mobile?: string;
}

export function Callback(props: Props) {
  const [firstName, setFirstName] = useState(props.firstName ?? '');
  const [mobile, setMobile] = useState(props.mobile ? formatPhoneInput(props.mobile) : '');
  const [reason, setReason] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [botToken, setBotToken] = useState<string | null>(null);
  const botWidget = useRef<TurnstileHandle>(null);

  const nameBad = firstName.trim().length === 0;
  const mobileBad = !isMobile(mobile);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (nameBad || mobileBad) return;
    if (botTokenMissing(props.bot, botToken)) {
      setError(BOT_WAIT_MESSAGE);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const msg = await props.api.callback({
        kind: props.kind,
        visitorId: props.visitorId,
        firstName: firstName.trim(),
        mobile,
        ...(props.typeId ? { typeId: props.typeId } : {}),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        transactionalConsent: consent,
        attribution: props.attribution,
        ...(botToken ? { turnstileToken: botToken } : {}),
      });
      setDone(msg);
    } catch (err) {
      setError(messageOf(err));
      if (botToken) botWidget.current?.reset();
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section className="bw-callback" data-kind={props.kind} aria-live="polite">
        <div className="bw-callback__done" data-testid="callback-done">
          <CheckCircle2 size={22} aria-hidden />
          <p>{done}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="bw-callback" data-kind={props.kind} aria-labelledby="bw-callback-title">
      <h3 id="bw-callback-title" className="bw-callback__title">
        {props.title}
      </h3>
      <p className="bw-dim">{props.lead}</p>
      {props.officePhone ? (
        <Button
          asChild
          variant={props.kind === 'emergency' ? 'primary' : 'secondary'}
          size="lg"
          block
        >
          <a href={telHref(props.officePhone)} data-testid="call-now">
            <Phone size={18} aria-hidden /> Call now {props.officePhoneLabel}
          </a>
        </Button>
      ) : null}
      <form className="bw-callback__form" onSubmit={(e) => void submit(e)} noValidate>
        <div className="bw-grid2">
          <Field
            label="First name"
            required
            error={tried && nameBad ? 'Enter your first name.' : undefined}
          >
            <Input
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              maxLength={60}
            />
          </Field>
          <Field
            label="Mobile number"
            required
            error={tried && mobileBad ? 'Enter a 10-digit mobile number.' : undefined}
          >
            <Input
              type="tel"
              inputMode="tel"
              autoComplete="tel-national"
              value={mobile}
              onChange={(e) => setMobile(formatPhoneInput(e.target.value))}
              placeholder="(555) 555-0100"
            />
          </Field>
        </div>
        <Field
          label="What is going on?"
          hint="Optional. Please do not include card or insurance numbers."
        >
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={300}
            rows={2}
          />
        </Field>
        <Checkbox
          checked={consent}
          onCheckedChange={(v) => setConsent(v === true)}
          label={`Text me about this request from ${props.practiceName}`}
          description="Message and data rates may apply. Reply STOP to opt out."
        />
        <BotCheck
          ref={botWidget}
          bot={props.bot}
          action={TURNSTILE_ACTIONS.bookingCallback}
          onToken={setBotToken}
        />
        {error ? (
          <p className="bw-error" role="alert">
            {error}
          </p>
        ) : null}
        <Button
          type="submit"
          variant={props.kind === 'emergency' ? 'secondary' : 'primary'}
          size="lg"
          block
          loading={busy}
        >
          Request a call
        </Button>
      </form>
    </section>
  );
}
